import { Box, FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, type DixaTypographySize } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import { PendingConversationsMetric } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

import {
  DashboardLegend,
  DashboardLegendNumberItem,
} from 'app/views/layout/views/dashboard/components/legend';
import {
  QueueDashboardMetric,
  type QueueDashboardMetricProps,
} from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';
import { AppLink } from 'components/appLink';
import { AppSearchParams } from 'utils/url/useAppSearchParams';

import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

import styles from './pendingConversationsMetric.module.scss';

const DIAMETER = 56;
const STROKE = 6;

type QueueDashboardPendingConversationsMetricProps = {
  metric?: PendingConversationsMetric;
} & PropsWithClassName;

export const QueueDashboardPendingConversationsMetric = memo(
  function QueueDashboardPendingConversationsMetric({
    className,
    metric,
  }: QueueDashboardPendingConversationsMetricProps): JSX.Element {
    const { queue, allQueueIds, isSkeleton } = useQueueDashboardQueueContext();

    /**
     * The component may be rendered in different contexts:
     *
     * - A single queue, linking to the conversations in that queue.
     * - Multiple queues, linking to the conversations in those queues.
     * - All queues, linking to all queued conversations.
     */
    const conversationsSearchParams = useMemo(
      (): AppSearchParams<'/conversations'> =>
        queue
          ? { queue: [queue.id] }
          : allQueueIds?.length
            ? { queue: [...allQueueIds] }
            : { queued: 'true' },
      [queue, allQueueIds],
    );

    const items = useMemo((): readonly DashboardLegendNumberItem[] => {
      return [
        {
          type: 'number',
          title: 'Waiting',
          format: 'number',
          value: metric?.in_queue ?? 0,
          color: colors.orange[500],
          linkProps: {
            path: '/conversations',
            params: {
              search: {
                ...conversationsSearchParams,
                status: ['open'],
                assigned: 'false',
              },
            },
            onClick: () => {
              ampli.dashboardQueuePendingConversationsMetricClicked({
                metric: 'in_queue',
                source: queue ? 'queue' : 'global',
              });
            },
          },
        },
        {
          type: 'number',
          title: 'Assigned',
          format: 'number',
          value: metric?.open ?? 0,
          color: colors.purple[400],
          linkProps: {
            path: '/conversations',
            params: {
              search: {
                ...conversationsSearchParams,
                status: ['open'],
                assigned: 'true',
              },
            },
            onClick: () => {
              ampli.dashboardQueuePendingConversationsMetricClicked({
                metric: 'open',
                source: queue ? 'queue' : 'global',
              });
            },
          },
        },
      ];
    }, [metric, queue, conversationsSearchParams]);

    const chartData = useMemo(
      () => items.filter(({ value }) => !isSkeleton && !!value),
      [isSkeleton, items],
    );

    const total = useMemo(
      (): number => chartData.reduce((acc, { value }) => acc + value, 0),
      [chartData],
    );

    const thresholdAlertTooltip =
      useMemo((): QueueDashboardMetricProps['alertTooltip'] => {
        if (!queue) return;

        const threshold = queue.queue_thresholds?.waiting_conversations;

        if (!metric?.threshold_breached || !threshold) {
          return null;
        }

        return {
          head: 'Maximum waiting in queue alert',
          content: (
            <>
              This queue has passed the maximum waiting conversations in queue,
              which is <b>{threshold}</b>.
            </>
          ),
        };
      }, [metric, queue]);

    const totalFontSize = useMemo((): DixaTypographySize => {
      if (total >= 100000) return '200';
      if (total >= 10000) return '300';
      if (total >= 1000) return '400';
      return '500';
    }, [total]);

    return (
      <QueueDashboardMetric
        className={className}
        metricType="pending_conversations"
        isSkeleton={isSkeleton}
        alertTooltip={thresholdAlertTooltip}
      >
        <FlexBox gap="250" alignItems="center">
          {isSkeleton ? (
            <Skeleton
              className={styles.skeleton}
              width={`${DIAMETER}px`}
              height={`${DIAMETER}px`}
              borderRadius="50%"
              flex="0 0 auto"
            >
              <Box
                width={`${DIAMETER - STROKE * 2}px`}
                height={`${DIAMETER - STROKE * 2}px`}
                background="neutral.000"
                borderRadius="50%"
              />
            </Skeleton>
          ) : (
            <PieChart width={DIAMETER} height={DIAMETER}>
              <Pie
                data={chartData.length ? chartData : [{ value: 1 }]} // Dummy value to render a full empty circle
                dataKey="value"
                innerRadius={DIAMETER / 2 - STROKE}
                outerRadius={DIAMETER / 2}
                startAngle={90} // Start at the 12:00 position
                endAngle={-270} // End at the 12:00 position
                fill={queue ? colors.neutral[200] : colors.purple[200]}
                stroke="none"
                isAnimationActive
              >
                {chartData.map((cell, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={cell.color}
                    strokeWidth={0}
                    style={{ outline: 'none' }}
                  />
                ))}
              </Pie>

              <foreignObject x="0" y="0" width={DIAMETER} height={DIAMETER}>
                <FlexBox
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  <AppLink
                    path="/conversations"
                    params={{
                      search: {
                        ...conversationsSearchParams,
                        status: ['open'],
                      },
                    }}
                    onClick={() => {
                      ampli.dashboardQueuePendingConversationsMetricClicked({
                        metric: 'total',
                        source: queue ? 'queue' : 'global',
                      });
                    }}
                  >
                    <Text.Paragraph
                      as="span"
                      variant="3"
                      color={total ? 'primary' : 'tertiary'}
                      fontSize={totalFontSize}
                    >
                      {formatInstrumentNumber(total, 'number')}
                    </Text.Paragraph>
                  </AppLink>
                </FlexBox>
              </foreignObject>
            </PieChart>
          )}

          <DashboardLegend items={items} isSkeleton={isSkeleton} />
        </FlexBox>
      </QueueDashboardMetric>
    );
  },
);
